import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  useTranslation,
} from 'react-i18next';

import AppIcon
  from '../../shared/ui/AppIcon';

import {
  PRODUCT_CATALOG,
} from '../product-catalog/productCatalog';

import { useAuth } from '../auth/AuthContext';
import LoginModal from '../auth/components/LoginModal';

import './ResourceLibraryPage.css';

const RESOURCE_TYPE_ICONS = {
  program:
    'software',
  manual:
    'manual',
  guide:
    'warning',
  introduction:
    'users',
  mobileQr:
    'software',
};

const MOBILE_QR_RESOURCE_ID =
  'hi-cps-mobile-qr';

const MOBILE_QR_IMAGES = Object.freeze({
  android:
    '/mobileQR/android_qrcode.png',
  ios:
    '/mobileQR/ios_qrcode.png',
});

function getExternalResourceUrl(
  resource,
) {
  return (
    resource?.downloadUrl ||
    resource?.url ||
    resource?.href ||
    null
  );
}

function normalizeLanguage(
  language,
) {
  return String(
    language ||
    'ko',
  )
    .trim()
    .toLowerCase()
    .split('-')[0];
}

function getLocalizedResourceFile(
  resource,
  language,
) {
  if (
    !resource?.files
  ) {
    return null;
  }

  const normalizedLanguage =
    normalizeLanguage(
      language,
    );

  if (
    normalizedLanguage ===
    'ko'
  ) {
    return (
      resource.files.ko ||
      resource.files.en ||
      null
    );
  }

  return (
    resource.files.en ||
    resource.files.ko ||
    null
  );
}

function isMobileQrResource(
  resource,
) {
  return (
    resource?.id ===
    MOBILE_QR_RESOURCE_ID ||
    resource?.type ===
    'mobileQr'
  );
}

export default function ResourceLibraryPage() {
  const { isAuthenticated } = useAuth();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  const {
    t,
    i18n,
  } =
    useTranslation(
      'app',
    );

  const [
    isQrModalOpen,
    setIsQrModalOpen,
  ] =
    useState(false);

  const currentLanguage =
    normalizeLanguage(
      i18n.resolvedLanguage ||
      i18n.language ||
      'ko',
    );

  const resourceGroups =
    useMemo(
      () =>
        PRODUCT_CATALOG
          .map(
            (
              product,
            ) => ({
              productId:
                product.id,
              productName:
                product.name,
              resources:
                Array.isArray(
                  product.resources,
                )
                  ? product.resources.filter(
                    (
                      resource,
                    ) =>
                      !isMobileQrResource(
                        resource,
                      ),
                  )
                  : [],
            }),
          )
          .filter(
            (
              group,
            ) =>
              group.resources.length >
              0,
          ),
      [],
    );

  useEffect(
    () => {
      document.title =
        `Hi-Portal`;
    },
    [
      t,
      i18n.resolvedLanguage,
    ],
  );

  useEffect(
    () => {
      if (
        !isQrModalOpen
      ) {
        return undefined;
      }

      const previousOverflow =
        document.body.style.overflow;

      document.body.style.overflow =
        'hidden';

      const handleKeyDown =
        (
          event,
        ) => {
          if (
            event.key ===
            'Escape'
          ) {
            setIsQrModalOpen(
              false,
            );
          }
        };

      document.addEventListener(
        'keydown',
        handleKeyDown,
      );

      return () => {
        document.body.style.overflow =
          previousOverflow;

        document.removeEventListener(
          'keydown',
          handleKeyDown,
        );
      };
    },
    [
      isQrModalOpen,
    ],
  );

  const handleResourceClick = async (
    resource,
    metadata,
  ) => {
    if (!isAuthenticated) {
      setIsLoginModalOpen(true);
      return;
    }

    try {
      if (
        resource?.resourceKey
      ) {
        const query =
          new URLSearchParams({
            key:
              resource.resourceKey,

            productId:
              metadata.productId,

            resourceId:
              metadata.resourceId,

            resourceType:
              metadata.resourceType,

            resourceName:
              metadata.resourceName,
          });

        const response =
          await fetch(
            `/api/resources/download-url?${query.toString()}`,
            {
              method:
                'GET',

              headers: {
                Accept:
                  'application/json',
              },

              credentials:
                'include',
            },
          );

        let payload =
          null;

        try {
          payload =
            await response.json();
        } catch {
          throw new Error(
            'Response is not valid JSON.',
          );
        }

        if (
          !response.ok
        ) {
          throw new Error(
            payload?.message ||
            `HTTP ${response.status}`,
          );
        }

        const signedUrl =
          payload?.data?.url;

        if (
          !signedUrl
        ) {
          throw new Error(
            'Failed to retrieve the download URL.',
          );
        }

        window.location.assign(
          signedUrl,
        );

        return;
      }

      const externalUrl =
        getExternalResourceUrl(
          resource,
        );

      if (
        externalUrl
      ) {
        window.open(
          externalUrl,
          '_blank',
          'noopener,noreferrer',
        );

        return;
      }

      window.alert(
        t(
          'resources.downloadPreparing',
        ),
      );
    } catch (
    error
    ) {
      console.error(
        '[ResourceLibraryPage] Download failed:',
        error,
      );
    }
  };

  const handleOpenQrModal =
    () => {
      setIsQrModalOpen(
        true,
      );
    };

  const handleCloseQrModal =
    () => {
      setIsQrModalOpen(
        false,
      );
    };

  return (
    <div
      className="resource-library-page"
    >
      <main
        className="resource-library-main"
      >
        <section
          className="resource-library-hero"
        >
          <div
            className="resource-library-hero-inner"
          >
            <span
              className="resource-library-eyebrow"
            >
              {'DOWNLOAD'}
            </span>

            <h1>
              {t(
                'resources.download',
              )}
            </h1>

            <p>
              {t(
                'resources.description',
              )}
            </p>
          </div>
        </section>

        <section
          className="resource-library-content"
        >
          <div
            className="resource-library-content-inner"
          >
            <div
              className="resource-product-list"
            >
              {resourceGroups.map(
                (
                  group,
                ) => (
                  <section
                    key={
                      group.productId
                    }
                    className="resource-product-section"
                  >
                    <header
                      className="resource-product-heading"
                    >
                      <div
                        className="resource-product-title-row"
                      >
                        <div>
                          <span
                            className="resource-product-label"
                          >
                            SOFTWARE
                          </span>

                          <h3>
                            {
                              group.productName
                            }
                          </h3>
                        </div>

                        {group.productId ===
                          'hi-cps' && (
                            <button
                              type="button"
                              className="resource-mobile-qr-button"
                              aria-label="Mobile QR"
                              title="Mobile QR"
                              onClick={
                                handleOpenQrModal
                              }
                            >
                              <AppIcon
                                type="software"
                                size={
                                  16
                                }
                              />

                              <span>
                                Mobile QR
                              </span>
                            </button>
                          )}
                      </div>
                    </header>

                    <div
                      className="resource-card-grid"
                    >
                      {group.resources.map(
                        (
                          resource,
                        ) => {
                          const localizedFile =
                            getLocalizedResourceFile(
                              resource,
                              currentLanguage,
                            );

                          const resourceName =
                            resource.nameKey
                              ? t(
                                resource.nameKey,
                                {
                                  defaultValue:
                                    localizedFile?.fileName ||
                                    resource.name ||
                                    resource.fileName ||
                                    resource.id,
                                },
                              )
                              : localizedFile?.fileName ||
                              resource.name ||
                              resource.fileName ||
                              resource.id;

                          const iconType =
                            RESOURCE_TYPE_ICONS[
                            resource.type
                            ] ||
                            'download';

                          return (
                            <article
                              key={
                                resource.id
                              }
                              className="resource-card"
                            >
                              <span
                                className="resource-card-icon"
                              >
                                <AppIcon
                                  type={
                                    iconType
                                  }
                                  size={
                                    22
                                  }
                                />
                              </span>

                              <div
                                className="resource-card-content"
                              >
                                <strong>
                                  {
                                    resourceName
                                  }
                                </strong>
                              </div>

                              {resource.type !== 'introduction' && (
                                <button
                                  type="button"
                                  className="resource-download-button"
                                  disabled={resource.disabled === true}
                                  aria-label={`${resourceName} ${t(
                                    'resources.download',
                                  )}`}
                                  title={t(
                                    'resources.download',
                                  )}
                                  onClick={() =>
                                    handleResourceClick(
                                      localizedFile ||
                                      resource,
                                      {
                                        productId:
                                          group.productId,

                                        resourceId:
                                          resource.id,

                                        resourceType:
                                          resource.type,

                                        resourceName,
                                      },
                                    )
                                  }
                                >
                                  <AppIcon
                                    type="download"
                                    size={
                                      17
                                    }
                                  />
                                </button>
                              )}
                            </article>
                          );
                        },
                      )}
                    </div>
                  </section>
                ),
              )}
            </div>
          </div>
        </section>
      </main>

      <LoginModal
        open={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
      />

      {isQrModalOpen && (
        <div
          className="mobile-qr-modal-overlay"
          role="presentation"
          onMouseDown={
            handleCloseQrModal
          }
        >
          <div
            className="mobile-qr-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-qr-modal-title"
            onMouseDown={(
              event,
            ) =>
              event.stopPropagation()
            }
          >
            <div
              className="mobile-qr-modal-header"
            >
              <div>
                <span>
                  Hi-CPS
                </span>

                <h3
                  id="mobile-qr-modal-title"
                >
                  Mobile QR
                </h3>
              </div>

              <button
                type="button"
                className="mobile-qr-modal-close"
                onClick={
                  handleCloseQrModal
                }
              >
                <span
                  aria-hidden="true"
                >
                  ×
                </span>
              </button>
            </div>

            <div
              className="mobile-qr-modal-content"
            >
              <div
                className="mobile-qr-item"
              >
                <div
                  className="mobile-qr-image-wrapper"
                >
                  <img
                    src={
                      MOBILE_QR_IMAGES
                        .android
                    }
                    alt="Hi-CPS Android QR Code"
                    className="mobile-qr-image"
                  />
                </div>

                <strong
                  className="mobile-qr-label"
                >
                  Android
                </strong>
              </div>

              <div
                className="mobile-qr-item"
              >
                <div
                  className="mobile-qr-image-wrapper"
                >
                  <img
                    src={
                      MOBILE_QR_IMAGES
                        .ios
                    }
                    alt="Hi-CPS iOS QR Code"
                    className="mobile-qr-image"
                  />
                </div>

                <strong
                  className="mobile-qr-label"
                >
                  iOS
                </strong>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
